/**
 * tm/pages/NewsletterPage.tsx — 「월간 소식지」 관리 (2026-10-09 지도사 「지원사업 클릭클릭 담아 두고 이달의 이슈 정해 두면 그 범주 안에서 뜨게」)
 *
 *  달마다 «담을 범위»를 고른다 — fphub · 세무사 사이트의 소식지 만들기는 이 범위(공개한 달) 안에서만 지원사업 · 이슈를 고른다.
 *  UI/UX 자문안(2026-10-09) 그대로:
 *   · 위 고정 줄: 발행 월 ‹ › · 공개 칩/버튼 · 저장 상태(자동 저장) · 지난달에서 가져오기 · 탭 두 개(지원사업 n | 이달의 이슈 n)
 *   · 지원사업 탭: 필터 185 | 후보 표(줄 클릭 = 담기/빼기, Shift+클릭 범위, ↑↓ Space, / 검색) | 범위 360(충족도 판 + 담은 목록)
 *   · 이달의 이슈 탭: 목록 260 | 입력 | 미리보기 340(안내장 왼쪽 아래 칸 실제 크기 · 줄 넘침 표시)
 *  규칙: 마감이 발행 월 1일보다 이른 공고는 후보에도 안 보인다 · 「마감 임박」 = 마감이 발행 월 안 · 「N월 접수」 = 접수 시작이 발행 월 뒤
 *  화면 문구에 「AI」 낱말 · 브라우저 alert/confirm 쓰지 않는다
 */
import React from 'react';
import { C, T, R, REGION_CITIES, Badge, Btn, SearchInput, IMEText, IMEArea, Empty, DDayBadge } from '../components/ui';
import {
  getGovPrograms, getNewsletter, getNewsletterMonths, saveNewsletter, getNewsletterUsage, getNewsletterIdeas, getNewsletterStale,
  type NewsletterCustom, type NewsletterIdea, type NewsletterIssue, type NewsletterIssueKind, type NewsletterMonth, type NewsletterMonthSummary, type NewsletterUsageRow,
} from '../api';
import type { GovProgram } from '../types';

// ═══ 날짜 · 분류 · 지역 도우미 ═══
const d10 = (v: string | null | undefined): string => (v ? String(v).slice(0, 10) : '');
const pad = (n: number): string => String(n).padStart(2, '0');
function ymOf(dt: Date): string { return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}`; }
function addYm(ym: string, k: number): string { const [y, m] = ym.split('-').map(Number); return ymOf(new Date(y, m - 1 + k, 1)); }
function monthRange(ym: string): { first: string; last: string } {
  const [y, m] = ym.split('-').map(Number);
  return { first: `${ym}-01`, last: `${ym}-${pad(new Date(y, m, 0).getDate())}` };
}
/** 처음 열 때 발행 월 — 20일부터는 다음 달 호를 준비한다 */
function defaultYm(): string { const t = new Date(); return t.getDate() >= 20 ? addYm(ymOf(t), 1) : ymOf(t); }
function ymLabel(ym: string): string { const [y, m] = ym.split('-').map(Number); return `${y}년 ${m}월호`; }
const md = (d: string): string => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}` : '');

const LOAN = ['직접대출', '대리대출', '이차보전', '보증지원'];
/** 분류 묶음 — 첫 분류 하나(「직접대출,매장환경 개선」 → 직접대출) · 정책자금은 앞에 「정책자금 ·」 */
function catOf(p: GovProgram): string {
  const c = String(p.category || '').split(/[,，/]/)[0].trim();
  if (!c) return '기타';
  return LOAN.includes(c) ? `정책자금 · ${c}` : c;
}
const SIDO = Object.keys(REGION_CITIES);
const SIDO_LONG: Record<string, string> = {
  서울특별시: '서울', 부산광역시: '부산', 대구광역시: '대구', 인천광역시: '인천', 광주광역시: '광주', 대전광역시: '대전', 울산광역시: '울산',
  세종특별자치시: '세종', 경기도: '경기', 강원도: '강원', 강원특별자치도: '강원', 충청북도: '충북', 충청남도: '충남', 전라북도: '전북',
  전북특별자치도: '전북', 전라남도: '전남', 경상북도: '경북', 경상남도: '경남', 제주특별자치도: '제주', 제주도: '제주',
};
/** 공고 지역 → 시도 목록 · 비었거나 「전국」이면 ['전국'] */
function sidosOf(p: GovProgram): string[] {
  let r = String(p.region || '').trim();
  Object.keys(SIDO_LONG).forEach((k) => { r = r.split(k).join(SIDO_LONG[k]); });
  if (!r || /전국/.test(r)) return ['전국'];
  const out = SIDO.filter((s) => r.includes(s));
  return out.length ? out : ['전국'];
}

/** 자격 요건 — 업종 · 지역 · 규모 말고 «아무 회사나 낼 수 없게» 만드는 조건 (2026-10-09 지도사 「요건 있는 것, 없는 것 선택해서 발라 볼 수 있게」)
 *  소식지는 업종 · 지역 · 규모로 고객에게 맞추므로, 그 밖의 조건이 없는 공고 = 그 조건의 고객이면 누구나 */
const REQ_KINDS = ['업력', '매출 · 직원', '대표 나이', '창업 여부', '전용 자격', '확인 항목'] as const;
type ReqKind = typeof REQ_KINDS[number];
type ReqFilter = '' | 'none' | 'any' | ReqKind;
const REQ_TIP: Record<ReqKind, string> = {
  업력: '업력 · 개업일 조건(예: 7년 이내 · 2020년 이후 개업)',
  '매출 · 직원': '연매출 · 상시근로자 · 매출 감소 · 수출실적 조건',
  '대표 나이': '대표 나이 조건(청년 · 시니어 등)',
  '창업 여부': '창업기업만 · 창업기업 제외',
  '전용 자격': '특정 기업 · 사람만(벤처 · 여성 · 장애인 · 졸업후보 등 인증 · 지정 · 「또는」 자격)',
  '확인 항목': '갖췄는지 따로 확인할 것(구내식당 · 기숙사 · 배출시설 등)',
};
const objOf = (v: unknown): Record<string, unknown> => {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
  if (typeof v === 'string' && v.trim().startsWith('{')) { try { const o: unknown = JSON.parse(v); return o && typeof o === 'object' ? o as Record<string, unknown> : {}; } catch { return {}; } }
  return {};
};
const listOf = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x || '').trim()).filter(Boolean) : []);
const numOk = (v: unknown): boolean => v != null && v !== '' && Number.isFinite(Number(v));
/** 규칙 묶음(공고 칸 · 유형 rules) 하나의 요건 종류 */
function reqKindsOfRules(r: Record<string, unknown>): Set<ReqKind> {
  const out = new Set<ReqKind>();
  const yrs = objOf(r.yrs), opened = objOf(r.opened), age = objOf(r.age);
  if (numOk(yrs.min) || numOk(yrs.max) || opened.before || opened.after) out.add('업력');
  if (Object.keys(objOf(r.sales)).length || Object.keys(objOf(r.emp)).length || Object.keys(objOf(r.drop)).length || Object.keys(objOf(r.exp)).length) out.add('매출 · 직원');
  if ((numOk(age.min) && Number(age.min) > 0) || (numOk(age.max) && Number(age.max) > 0)) out.add('대표 나이');
  if (r.startup === true || r.startup === 'exclude') out.add('창업 여부');
  const off = new Set(listOf(r.sec_off));
  const sec = [...listOf(r.sec), ...listOf(r.spec), ...listOf(r.types)].filter((k) => !k.includes('›') && !off.has(k));   // 「›」 = 하위 업종(업종으로 거른다)
  if (sec.length || (Array.isArray(r.any) && r.any.length)) out.add('전용 자격');
  if (listOf(r.chk).length) out.add('확인 항목');
  return out;
}
function reqsOf(p: GovProgram): ReqKind[] {
  const X = objOf(p.conditions);
  const out = reqKindsOfRules(X);
  if (numOk(p.target_yrs_min) || numOk(p.target_yrs_max)) out.add('업력');
  if ((p.target_sales_op && numOk(p.target_sales_eok)) || (p.target_emp_op && numOk(p.target_emp_n))) out.add('매출 · 직원');
  if ((numOk(p.target_ceo_age_max) && Number(p.target_ceo_age_max) > 0) || (numOk(p.target_ceo_age_min) && Number(p.target_ceo_age_min) > 0)) out.add('대표 나이');
  if (/^(창업기업만|only|비창업만|exclude)$/.test(String(p.target_startup || '').trim())) out.add('창업 여부');
  if (p.matching_tags && Array.isArray(p.matching_tags.required) && p.matching_tags.required.length) out.add('전용 자격');
  // 세부 유형이 있으면 — 모든 유형에 같은 종류 요건이 있을 때만(한 유형이라도 없으면 그 종류는 누구나 낼 길이 있다)
  const tr = Array.isArray(p.tracks) ? p.tracks.filter((t) => t && t.form !== 'consult') : [];
  if (tr.length) {
    const sets = tr.map((t) => reqKindsOfRules(objOf(t.rules)));
    REQ_KINDS.forEach((k) => { if (sets.every((st) => st.has(k))) out.add(k); });
  }
  return REQ_KINDS.filter((k) => out.has(k));
}

/** 공고 업종(상위) — 소식지 만들기의 업종 거르기와 같은 값(newsletterBrief bizOf) · 비면 업종 무관 */
const bizTop = (p: GovProgram): string[] => Array.from(new Set((Array.isArray(p.target_biz_types) ? p.target_biz_types : []).map((b) => String(b).split('›')[0].trim()).filter((b) => b && !/^(전업종|무관|전체)$/.test(b))));
/** 공고 시·군·구 한정(region_detail) — newsletterBrief sggOf 와 같은 값 */
const sggList = (p: GovProgram): string[] => {
  const v: unknown = p.region_detail;
  const L = Array.isArray(v) ? v.map(String) : String(v || '').split(/[,·/\s]+/);
  return L.map((x) => x.trim()).filter(Boolean);
};
type Status = 'urgent' | 'later' | 'always' | 'open';
function statusOf(p: GovProgram, ym: string): Status {
  const { last } = monthRange(ym);
  const dl = d10(p.deadline), st = d10(p.apply_start);
  if (st && st > last) return 'later';
  if (!dl) return 'always';
  if (dl <= last) return 'urgent';
  return 'open';
}
function StatusTag({ p, ym }: { p: GovProgram; ym: string }) {
  const s = statusOf(p, ym);
  if (s === 'urgent') return <Badge bg={C.orangeLt} color={C.orange}>마감 임박</Badge>;
  if (s === 'later') return <Badge bg={C.purpleLt} color={C.purple}>{Number(d10(p.apply_start).slice(5, 7))}월 접수</Badge>;
  if (s === 'always') return <Badge bg={C.bgAlt} color={C.sub}>{p.first_come ? '선착순' : '상시'}</Badge>;
  return p.first_come ? <Badge bg={C.white} color={C.red} style={{ border: `1px solid ${C.red}44` }}>선착순</Badge> : null;
}
/** 오늘(이 컴퓨터 날짜) 'YYYY-MM-DD' */
const todayYmd = (): string => { const t = new Date(); return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`; };
/** 쓸 수 있는 공고 — 마감이 발행 월 1일 · 오늘 중 늦은 날 이후(상시는 늘) · 이미 마감된 건 안 보인다(2026-10-10 지도사 「마감된 거 보여 주면 안 되고」 — 서버 usableIn 과 같은 규칙) */
const usable = (p: GovProgram, ym: string): boolean => {
  const dl = d10(p.deadline);
  if (!dl) return true;
  const first = monthRange(ym).first, t = todayYmd();
  return dl >= (t > first ? t : first);
};

// ═══ 직접 등록(여기서만 · 2026-10-10 지도사 「여기서 내가 등록도 할 수 있게 — 정부지원금 리포트 등 다른 데 영향 안 주고 오로지 여기만」) ═══
//  newsletter_months.customs 에만 저장 · id 'nl-…' · 정부지원사업 탭 · 리포트 · 매칭에는 안 들어간다
const isCustom = (id: string): boolean => id.startsWith('nl-');
const newCustomId = (): string => `nl-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const blankCustom = (): NewsletterCustom => ({ id: newCustomId(), name: '', agency: '', amount: '', category: '', areas: {}, region: '전국', sizes: [], biz: [], apply_start: null, deadline: null, first_come: false, link: '' });
/** 소식지 만들기의 고객 업종 5가지 → 공고 업종(newsletterBrief INDUSTRIES 와 같게) */
const NL_BIZ: Record<string, string[]> = { 제조업: ['제조업'], 도소매업: ['도소매업'], 건설업: ['건설업'], '음식·숙박업': ['음식점업', '숙박업'], 'IT·서비스업': ['IT/SW개발업', '연구개발업', '서비스업'] };
const NL_SIZES = ['소상공인', '소기업', '중기업'];
/** 저장된 직접 등록의 지역 → areas (옛 region 글도) */
function areasOf(c: NewsletterCustom): Record<string, string[]> {
  if (c.areas && typeof c.areas === 'object') return c.areas;
  const out: Record<string, string[]> = {};
  SIDO.filter((s) => String(c.region || '').includes(s)).forEach((s) => { out[s] = []; });
  const d = String(c.region_detail || '').split(/[,·/\s]+/).filter(Boolean), ks = Object.keys(out);
  if (ks.length === 1 && d.length) out[ks[0]] = d;
  return out;
}
const areaText = (A: Record<string, string[]>): string => { const ks = SIDO.filter((s) => A[s]); return ks.length ? ks.map((s) => (A[s].length ? `${s} ${A[s].join('·')}` : s)).join(' / ') : '전국'; };
function customProg(c: NewsletterCustom): GovProgram {
  return {
    id: c.id, account_id: '', name: c.name, report_name: c.name, brief_name: c.name, deadline: c.deadline, agency: c.agency, amount: c.amount, category: c.category,
    region: SIDO.filter((s) => areasOf(c)[s]).join(',') || '전국', region_detail: SIDO.flatMap((s) => areasOf(c)[s] || []).join(','), tags: [],
    target_biz_types: Array.from(new Set((c.biz || []).flatMap((g) => NL_BIZ[g] || []))),
    target_company_scale: (c.sizes || []).length ? (c.sizes || []).join('·') : '', target_startup: '',
    apply_start: c.apply_start, matching_tags: null, link: c.link, short_url: '', first_come: c.first_come, is_active: true, memo: '',
    created_at: c.created_at || '', updated_at: '',
  };
}
const byDeadline = (a: GovProgram, b: GovProgram): number => {
  const x = d10(a.deadline) || '9999', y = d10(b.deadline) || '9999';
  return x < y ? -1 : x > y ? 1 : String(a.brief_name || a.name).localeCompare(String(b.brief_name || b.name));
};
const shortName = (p: GovProgram): string => String(p.brief_name || p.report_name || p.name || '');
const periodOf = (p: GovProgram): string => {
  const st = d10(p.apply_start), dl = d10(p.deadline);
  if (!dl) return st ? `${md(st)} –` : '';
  return st ? `${md(st)} – ${md(dl)}` : `– ${md(dl)}`;
};

/** 안내장 글꼴(Wanted Sans · OFL) — public/newsletter/fonts 의 KS X 1001 한글 부분 글꼴(미리보기용) */
const FONT_CSS = [['Regular', 400], ['SemiBold', 600], ['Bold', 700], ['ExtraBold', 800]]
  .map(([n, w]) => `@font-face{font-family:'Wanted Sans';src:url('/newsletter/fonts/WantedSans-${n}-ksx.woff') format('woff');font-weight:${w};font-display:swap}`).join('');

// ═══ 저장 상태 ═══
type SaveState = { k: 'idle' } | { k: 'saving' } | { k: 'saved'; at: string } | { k: 'fail'; msg: string };
const nowHm = (): string => { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };

const KINDS: NewsletterIssueKind[] = ['세법', '법령', '제도', '판례', '유권해석', '정책'];
const newId = (): string => `iss-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const blankIssue = (): NewsletterIssue => ({ id: newId(), kind: '세법', label: '', caption: '', title: '', body: '', action: '', link: '', star: false });

export default function NewsletterPage() {
  const [ym, setYm] = React.useState<string>(defaultYm);
  const [tab, setTab] = React.useState<'prog' | 'issue' | 'usage'>('prog');
  const [all, setAll] = React.useState<GovProgram[] | null>(null);
  const [loadErr, setLoadErr] = React.useState('');
  const [doc, setDoc] = React.useState<NewsletterMonth | null>(null);
  const [months, setMonths] = React.useState<NewsletterMonthSummary[]>([]);
  const [save, setSave] = React.useState<SaveState>({ k: 'idle' });
  const [toast, setToast] = React.useState<{ msg: string; undo?: () => void } | null>(null);
  const [importOpen, setImportOpen] = React.useState(false);

  // ── 자동 저장: 바뀐 칸만 모아 보낸다(지원사업 0.3초 · 이슈 1초 뒤) ──
  const docRef = React.useRef<NewsletterMonth | null>(null);
  docRef.current = doc;
  const dirty = React.useRef<Set<'program_ids' | 'issues' | 'customs' | 'published'>>(new Set());
  const timer = React.useRef<number | null>(null);
  const flushing = React.useRef<Promise<void> | null>(null);

  const flush = React.useCallback(async (): Promise<void> => {
    if (timer.current != null) { window.clearTimeout(timer.current); timer.current = null; }
    if (flushing.current) await flushing.current;
    const d = docRef.current;
    if (!d || !dirty.current.size) return;
    const keys = Array.from(dirty.current); dirty.current.clear();
    const patch: Partial<Pick<NewsletterMonth, 'program_ids' | 'issues' | 'customs' | 'published'>> = {};
    keys.forEach((k) => { if (k === 'program_ids') patch.program_ids = d.program_ids; else if (k === 'issues') patch.issues = d.issues; else if (k === 'customs') patch.customs = d.customs || []; else patch.published = d.published; });
    setSave({ k: 'saving' });
    const run = (async () => {
      try {
        await saveNewsletter(d.ym, patch);
        setSave({ k: 'saved', at: nowHm() });
        getNewsletterMonths().then((r) => setMonths(r.data || [])).catch(() => undefined);
      } catch (e) {
        keys.forEach((k) => dirty.current.add(k));   // 다시 시도 때 같이 보냄
        setSave({ k: 'fail', msg: e instanceof Error ? e.message : '저장 실패' });
      }
    })();
    flushing.current = run;
    await run;
    flushing.current = null;
  }, []);
  const schedule = React.useCallback((key: 'program_ids' | 'issues' | 'customs' | 'published', ms: number) => {
    dirty.current.add(key);
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { flush(); }, ms);
  }, [flush]);

  // ── 불러오기 ──
  React.useEffect(() => {
    getGovPrograms().then((r) => setAll((r.data || []).filter((p) => p.is_active !== false && p.dup_primary !== false)))
      .catch((e: unknown) => setLoadErr(e instanceof Error ? e.message : '공고를 불러오지 못했습니다'));
    getNewsletterMonths().then((r) => setMonths(r.data || [])).catch(() => undefined);
  }, []);
  React.useEffect(() => {
    let alive = true;
    setDoc(null);
    getNewsletter(ym).then((r) => { if (alive) setDoc(r.data); })
      .catch((e: unknown) => { if (alive) setLoadErr(e instanceof Error ? e.message : '소식지를 불러오지 못했습니다'); });
    return () => { alive = false; };
  }, [ym]);
  // 그 해 공고만 — 발행 연도가 아닌(지난해) 공고는 후보에서 빼고, 이미 담긴 것도 빼 둔다(되돌리기 가능) (2026-10-09 지도사 「2026년 공고만」)
  const [stale, setStale] = React.useState<{ y: string; ids: Set<string> } | null>(null);
  React.useEffect(() => {
    const y = ym.slice(0, 4);
    if (stale && stale.y === y) return undefined;
    let alive = true;
    getNewsletterStale(ym).then((r) => { if (alive) setStale({ y, ids: new Set((r.data || []).map((x) => x.id)) }); }).catch(() => undefined);
    return () => { alive = false; };
  }, [ym]);   // eslint-disable-line react-hooks/exhaustive-deps
  const pruned = React.useRef('');
  React.useEffect(() => {
    if (!doc || !stale || stale.y !== ym.slice(0, 4) || pruned.current === ym) return;
    pruned.current = ym;
    const off = doc.program_ids.filter((id) => stale.ids.has(id));
    if (!off.length) return;
    const before = doc.program_ids;
    setIds(before.filter((id) => !stale.ids.has(id)));
    setToast({ msg: `지난해 공고 ${off.length}건을 뺐습니다 · 이달의 지원사업은 ${ym.slice(0, 4)}년 공고만`, undo: () => setIds(before) });
  }, [doc, stale, ym]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => () => { flush(); }, [flush]);   // 화면을 떠날 때 남은 저장
  React.useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirty.current.size) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);
  React.useEffect(() => { if (!toast) return undefined; const t = window.setTimeout(() => setToast(null), 6000); return () => window.clearTimeout(t); }, [toast]);

  const goYm = async (next: string) => { await flush(); setImportOpen(false); setYm(next); };

  const setIds = (ids: string[]) => { setDoc((d) => (d ? { ...d, program_ids: ids } : d)); schedule('program_ids', 300); };
  const setIssues = (L: NewsletterIssue[]) => { setDoc((d) => (d ? { ...d, issues: L } : d)); schedule('issues', 1000); };
  const setCustoms = (L: NewsletterCustom[]) => { setDoc((d) => (d ? { ...d, customs: L } : d)); schedule('customs', 300); };
  const setPublished = (v: boolean) => { setDoc((d) => (d ? { ...d, published: v } : d)); schedule('published', 0); };

  const customs = React.useMemo(() => (doc && doc.customs) || [], [doc]);
  const customProgs = React.useMemo(() => customs.map(customProg), [customs]);
  const byId = React.useMemo(() => { const m = new Map<string, GovProgram>(); (all || []).forEach((p) => m.set(p.id, p)); customProgs.forEach((p) => m.set(p.id, p)); return m; }, [all, customProgs]);
  const fresh = React.useMemo(() => (all && stale && stale.y === ym.slice(0, 4) ? all.filter((p) => !stale.ids.has(p.id)) : all), [all, stale, ym]);   // 후보 = 그 해 공고만
  const cand = React.useMemo(() => [...customProgs, ...(fresh || all || [])], [customProgs, fresh, all]);   // 직접 등록은 맨 앞에 함께
  const pool = React.useMemo(() => (doc ? doc.program_ids.map((id) => byId.get(id)).filter((p): p is GovProgram => !!p) : []), [doc, byId]);

  const saveText = save.k === 'saving' ? <span style={{ color: C.muted }}>저장 중…</span>
    : save.k === 'saved' ? <span style={{ color: C.green, fontWeight: 600 }}>✓ 저장됨 {save.at}</span>
      : save.k === 'fail' ? <button onClick={() => flush()} style={{ ...linkBtn, color: C.red }} title={save.msg}>저장 실패 · 다시 시도</button>
        : <span style={{ color: C.muted }}>바꾸면 바로 저장됩니다</span>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <style>{FONT_CSS}</style>
      {/* ── 위 줄 — 정부지원사업 탭과 같은 모양(GovTopBar · 탭 전환 · 상담 캘린더와 같은 ◀ 월 ▶) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', minHeight: 50, boxSizing: 'border-box', borderBottom: `1px solid ${C.border}`, flexShrink: 0, flexWrap: 'wrap' }}>
        <div role="tablist" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: R.md, background: '#E9EBEF', flexShrink: 0 }}>
          {([['prog', '지원사업', doc ? doc.program_ids.length : null], ['issue', '이달의 이슈', doc ? doc.issues.length : null], ['usage', '사용 현황', null]] as const).map(([k, l, n]) => {
            const act = tab === k;
            return (
              <button key={k} role="tab" aria-selected={act} onClick={() => setTab(k)} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: act ? C.white : 'transparent', boxShadow: act ? '0 1px 2px rgba(17,24,39,0.10)' : 'none',
                color: act ? C.text : C.sub, fontSize: T.sm, fontWeight: act ? 700 : 500, whiteSpace: 'nowrap',
              }}>
                {l}{n != null && <span style={{ fontSize: T.xs, fontWeight: 700, color: act ? C.accent : C.muted, fontVariantNumeric: 'tabular-nums' }}>{n}</span>}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 6 }}>
          <button aria-label="이전 달" onClick={() => goYm(addYm(ym, -1))} style={monthBtn}>◀</button>
          <select value={ym} onChange={(e) => goYm(e.target.value)} aria-label="발행 월"
            style={{ fontSize: T.lg, fontWeight: 800, color: C.text, border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 0', fontFamily: 'inherit' }}>
            {monthOptions(ym, months).map((o) => <option key={o.ym} value={o.ym}>{ymLabel(o.ym)}{o.tag}</option>)}
          </select>
          <button aria-label="다음 달" onClick={() => goYm(addYm(ym, 1))} style={monthBtn}>▶</button>
        </div>
        {doc && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title={doc.published ? '고친 내용이 fphub · 세무사 사이트에 바로 반영됩니다' : '공개해야 fphub · 세무사 사이트에서 이 달 범위를 씁니다'}>
            {doc.published ? <Badge bg={C.greenLt} color={C.green}>공개 중</Badge> : <Badge bg={C.bgAlt} color={C.sub} style={{ border: `1px solid ${C.border}` }}>초안</Badge>}
            {doc.published
              ? <Btn variant="secondary" onClick={() => setPublished(false)} style={{ fontSize: T.xs, padding: '6px 10px' }}>공개 멈추기</Btn>
              : <Btn onClick={() => setPublished(true)} style={{ fontSize: T.xs, padding: '6px 12px' }}>공개하기</Btn>}
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, fontSize: T.xs }}>
          {saveText}
          <Btn variant="secondary" onClick={() => setImportOpen(!importOpen)} disabled={!doc || !all} style={{ fontSize: T.xs, padding: '6px 10px' }}>{importOpen ? '가져오기 닫기' : '지난달에서 가져오기'}</Btn>
        </div>
      </div>

      {loadErr && <div role="alert" style={{ background: C.redLt, color: C.red, padding: '8px 16px', fontSize: T.sm, borderBottom: `1px solid ${C.red}22` }}>{loadErr}</div>}
      {save.k === 'fail' && <div role="alert" style={{ background: C.redLt, color: C.red, padding: '8px 16px', fontSize: T.sm, borderBottom: `1px solid ${C.red}22` }}>저장하지 못한 내용이 있습니다 — 위의 「다시 시도」를 눌러 주세요 ({save.msg})</div>}

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {tab === 'usage' ? <UsageTab ym={ym} />
          : !doc || !all ? <div style={{ margin: 'auto', textAlign: 'center', color: C.muted, fontSize: T.md }}><div style={{ fontSize: 36, marginBottom: 8 }}>⏳</div>불러오는 중…</div>
          : tab === 'prog'
            ? <ProgramTab ym={ym} all={cand} ids={doc.program_ids} pool={pool} setIds={setIds} onToast={setToast}
              importOpen={importOpen} setImportOpen={setImportOpen} byId={byId} issues={doc.issues} setIssues={setIssues}
              customs={customs} setCustoms={setCustoms} setDocBoth={(L, ids) => { setDoc((d) => (d ? { ...d, customs: L, program_ids: ids } : d)); schedule('customs', 0); schedule('program_ids', 300); }} />
            : <IssueTab issues={doc.issues} setIssues={setIssues} importOpen={importOpen} setImportOpen={setImportOpen}
              ym={ym} byId={byId} ids={doc.program_ids} setIds={setIds} onToast={setToast} customs={customs} setCustoms={setCustoms} />}
      </div>

      {toast && (
        <div role="status" style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', background: C.text, color: C.white, borderRadius: R.md, padding: '12px 24px', fontSize: T.sm, fontWeight: 600, display: 'flex', gap: 14, alignItems: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}>
          {toast.msg}
          {toast.undo && <button onClick={() => { const u = toast.undo; setToast(null); if (u) u(); }} style={{ ...linkBtn, color: '#93C5FD' }}>되돌리기</button>}
        </div>
      )}
    </div>
  );
}

/** 발행 월 고르기 목록 — 저장된 달 + 앞뒤 몇 달 */
function monthOptions(cur: string, saved: NewsletterMonthSummary[]): { ym: string; tag: string }[] {
  const set = new Map<string, string>();
  for (let k = -3; k <= 3; k++) set.set(addYm(defaultYm(), k), '');
  saved.forEach((s) => set.set(s.ym, s.ym === cur ? '' : ` · ${s.published ? '공개' : '초안'} ${s.n_programs}건`));   // 고른 달은 이름만(위 줄에 상태 칩이 따로 있음)
  if (!set.has(cur)) set.set(cur, '');
  return Array.from(set.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([y, tag]) => ({ ym: y, tag }));
}

// ═══ 사용 현황 탭 (2026-10-09 지도사 「어느 세무사사무소에서 언제 생성했는지 간단히」) ═══
const SITE_LABEL: Record<NewsletterUsageRow['site'], string> = { tax: '세무사', fphub: 'fphub', atv: '올댓벤처스' };
function UsageTab({ ym }: { ym: string }) {
  const [only, setOnly] = React.useState(true);
  const [rows, setRows] = React.useState<NewsletterUsageRow[] | null>(null);
  const [err, setErr] = React.useState('');
  React.useEffect(() => {
    let alive = true;
    setRows(null); setErr('');
    getNewsletterUsage(only ? ym : undefined).then((r) => { if (alive) setRows(r.data || []); })
      .catch((e: unknown) => { if (alive) setErr(e instanceof Error ? e.message : '불러오지 못했습니다'); });
    return () => { alive = false; };
  }, [ym, only]);
  const offices = rows ? new Set(rows.map((r) => `${r.site}|${r.office}`)).size : 0;
  const fmt = (v: string): string => { const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`; };
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div role="radiogroup" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: R.md, background: '#E9EBEF' }}>
          {([[true, ymLabel(ym)], [false, '전체 기간']] as const).map(([v, l]) => {
            const on = only === v;
            return <button key={l} role="radio" aria-checked={on} onClick={() => setOnly(v)} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: T.sm, fontWeight: on ? 700 : 500, background: on ? C.white : 'transparent', boxShadow: on ? '0 1px 2px rgba(17,24,39,0.10)' : 'none', color: on ? C.text : C.sub }}>{l}</button>;
          })}
        </div>
        {rows && <span style={{ fontSize: T.xs, color: C.sub }}>만든 횟수 <b style={{ color: C.text }}>{rows.length}</b> · 사무소 <b style={{ color: C.text }}>{offices}</b>곳</span>}
      </div>
      <div style={{ borderRadius: R.lg, background: C.white, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 80px minmax(0,1fr) 120px 110px 70px', gap: 8, padding: '9px 14px', background: C.bgAlt, borderBottom: `1px solid ${C.border}`, fontSize: T.xs, fontWeight: 700, color: C.sub }}>
          <span>언제</span><span>사이트</span><span>사무소</span><span>담당자</span><span>발행 월</span><span>파일</span>
        </div>
        {err && <div style={{ padding: 16, color: C.red, fontSize: T.sm }}>{err}</div>}
        {!rows && !err && <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: T.sm }}>불러오는 중…</div>}
        {rows && !rows.length && <Empty icon="📭" message="아직 만든 기록이 없습니다 — 세무사 사이트 · fphub 에서 소식지를 저장하면 여기에 쌓입니다" />}
        {rows && rows.map((r) => (
          <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '120px 80px minmax(0,1fr) 120px 110px 70px', gap: 8, padding: '9px 14px', borderBottom: `1px solid ${C.border}`, fontSize: T.sm, alignItems: 'center' }}>
            <span style={{ color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.created_at)}</span>
            <span>{r.site === 'tax' ? <Badge bg={C.accentLt} color={C.accent}>{SITE_LABEL.tax}</Badge> : r.site === 'atv' ? <Badge bg={C.purpleLt} color={C.purple}>{SITE_LABEL.atv}</Badge> : <Badge bg={C.greenLt} color={C.green}>{SITE_LABEL.fphub}</Badge>}</span>
            <span style={{ fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.office || '(이름 없음)'}</span>
            <span style={{ color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.person || '-'}</span>
            <span style={{ color: C.sub }}>{ymLabel(r.ym)}</span>
            <span><Badge bg={C.bgAlt} color={C.sub} style={{ border: `1px solid ${C.border}` }}>{r.kind.toUpperCase()}</Badge></span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══ 지원사업 탭 ═══
interface ProgramTabProps {
  ym: string; all: GovProgram[]; ids: string[]; pool: GovProgram[]; setIds: (ids: string[]) => void;
  onToast: (t: { msg: string; undo?: () => void }) => void;
  importOpen: boolean; setImportOpen: (v: boolean) => void; byId: Map<string, GovProgram>;
  customs: NewsletterCustom[]; setCustoms: (L: NewsletterCustom[]) => void; setDocBoth: (L: NewsletterCustom[], ids: string[]) => void;
  issues: NewsletterIssue[]; setIssues: (L: NewsletterIssue[]) => void;
}
type StFilter = 'all' | 'urgent' | 'later' | 'always';
function ProgramTab({ ym, all, ids, pool, setIds, onToast, importOpen, setImportOpen, byId, issues, setIssues, customs, setCustoms, setDocBoth }: ProgramTabProps) {
  const [editing, setEditing] = React.useState<NewsletterCustom | null>(null);
  const saveCustom = (c: NewsletterCustom) => {
    const exists = customs.some((x) => x.id === c.id);
    const L = exists ? customs.map((x) => (x.id === c.id ? c : x)) : [c, ...customs];
    setDocBoth(L, ids.includes(c.id) ? ids : [...ids, c.id]);
    setEditing(null);
    onToast({ msg: exists ? `「${c.name}」 고침` : `「${c.name}」 등록하고 담음 · 이 소식지에만 쓰입니다` });
  };
  const removeCustom = (c: NewsletterCustom) => {
    const beforeC = customs, beforeI = ids;
    setDocBoth(customs.filter((x) => x.id !== c.id), ids.filter((x) => x !== c.id));
    if (editing && editing.id === c.id) setEditing(null);
    onToast({ msg: `「${c.name}」 지움`, undo: () => setDocBoth(beforeC, beforeI) });
  };
  const [q, setQ] = React.useState('');
  const [sido, setSido] = React.useState<string>('');
  const [withNation, setWithNation] = React.useState(true);
  const [cat, setCat] = React.useState<string>('');
  const [st, setSt] = React.useState<StFilter>('all');
  const [req, setReq] = React.useState<ReqFilter>('');
  const [hideIn, setHideIn] = React.useState(false);
  const [onlyIn, setOnlyIn] = React.useState(false);   // 「담은 것」만 보기 (2026-10-10 지도사 「담긴 것들을 보는 화면이 없네」)
  const [cursor, setCursor] = React.useState(0);
  const [armAll, setArmAll] = React.useState(false);
  const [armClear, setArmClear] = React.useState(false);   // 「보이는 N건 모두 빼기」 두 번 눌러 확인 (2026-10-09 지도사 「모두 비우기 기능이 없네」)
  const lastClick = React.useRef<number | null>(null);
  const searchWrap = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const inSet = React.useMemo(() => new Set(ids), [ids]);
  // 「담은 것」 보기에 들어올 때 담겨 있던 것 — 보기 안에서 눌러 빼도 카드는 그 자리에 남는다(다시 누르면 다시 담김) · 보기를 바꾸거나 달을 바꾸면 비운다 (2026-10-10 지도사 「클릭한다고 사라져버려 그러면 안되지」)
  const [kept, setKept] = React.useState<Set<string>>(() => new Set());
  React.useEffect(() => { setKept(new Set()); }, [ym]);

  const cands = React.useMemo(() => all.filter((p) => usable(p, ym)), [all, ym]);
  const cats = React.useMemo(() => {
    const m = new Map<string, number>(); cands.forEach((p) => m.set(catOf(p), (m.get(catOf(p)) || 0) + 1));
    return Array.from(m.entries()).sort((a, b) => (a[0].startsWith('정책자금') === b[0].startsWith('정책자금') ? b[1] - a[1] : a[0].startsWith('정책자금') ? 1 : -1));
  }, [cands]);

  const sidoCount = React.useMemo(() => {
    const m = new Map<string, number>(); cands.forEach((p) => sidosOf(p).forEach((x) => m.set(x, (m.get(x) || 0) + 1)));
    return m;
  }, [cands]);
  const reqMap = React.useMemo(() => new Map(cands.map((p) => [p.id, reqsOf(p)])), [cands]);
  const reqCount = React.useMemo(() => {
    const m = new Map<string, number>();
    cands.forEach((p) => { const k = reqMap.get(p.id) || []; m.set(k.length ? 'any' : 'none', (m.get(k.length ? 'any' : 'none') || 0) + 1); k.forEach((x) => m.set(x, (m.get(x) || 0) + 1)); });
    return m;
  }, [cands, reqMap]);
  const rows = React.useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const L = cands.filter((p) => {
      if (onlyIn ? !(inSet.has(p.id) || kept.has(p.id)) : hideIn && inSet.has(p.id)) return false;
      if (sido) { const S = sidosOf(p); if (!(S.includes(sido) || (withNation && S.includes('전국')))) return false; }
      if (cat && catOf(p) !== cat) return false;
      if (st !== 'all' && statusOf(p, ym) !== st) return false;
      if (req) { const k = reqMap.get(p.id) || []; if (req === 'none' ? k.length > 0 : req === 'any' ? !k.length : !k.includes(req)) return false; }
      if (words.length) { const h = `${p.name} ${p.brief_name || ''} ${p.agency} ${p.region} ${p.region_detail || ''} ${p.category}`.toLowerCase(); if (!words.every((w) => h.includes(w))) return false; }
      return true;
    }).sort(byDeadline);
    return [...L.filter((p) => d10(p.deadline)), ...L.filter((p) => !d10(p.deadline))];   // 상시는 맨 아래 따로
  }, [cands, q, sido, withNation, cat, st, req, reqMap, hideIn, onlyIn, inSet, kept, ym]);
  const rowsIn = React.useMemo(() => rows.filter((p) => inSet.has(p.id)).length, [rows, inSet]);
  const firstAlways = rows.findIndex((p) => !d10(p.deadline));
  React.useEffect(() => { setCursor((c) => Math.min(c, Math.max(0, rows.length - 1))); }, [rows.length]);
  React.useEffect(() => { if (!armAll) return undefined; const t = window.setTimeout(() => setArmAll(false), 3000); return () => window.clearTimeout(t); }, [armAll]);
  React.useEffect(() => { if (!armClear) return undefined; const t = window.setTimeout(() => setArmClear(false), 3000); return () => window.clearTimeout(t); }, [armClear]);

  // 「/」 = 검색창
  React.useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tg = e.target as HTMLElement | null;
      if (e.key === '/' && !(tg && /INPUT|TEXTAREA|SELECT/.test(tg.tagName))) { e.preventDefault(); const el = searchWrap.current && searchWrap.current.querySelector('input'); if (el) el.focus(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const toggle = (p: GovProgram) => setIds(inSet.has(p.id) ? ids.filter((x) => x !== p.id) : [...ids, p.id]);
  const onRowClick = (i: number, e: React.MouseEvent) => {
    setCursor(i);
    if (e.shiftKey && lastClick.current != null) {
      const [a, b] = [Math.min(lastClick.current, i), Math.max(lastClick.current, i)];
      const add = rows.slice(a, b + 1).map((p) => p.id).filter((id) => !inSet.has(id));
      if (add.length) { setIds([...ids, ...add]); onToast({ msg: `${add.length}건 담음`, undo: () => setIds(ids) }); }
    } else toggle(rows[i]);
    lastClick.current = i;
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(rows.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); if (rows[cursor]) { toggle(rows[cursor]); lastClick.current = cursor; } }
  };
  React.useEffect(() => {
    const el = listRef.current && listRef.current.querySelector(`[data-i="${cursor}"]`);
    if (el && 'scrollIntoView' in el) (el as HTMLElement).scrollIntoView({ block: 'nearest' });
  }, [cursor]);
  const visibleNew = rows.filter((p) => !inSet.has(p.id));
  const addAllVisible = () => {
    if (!armAll) { setArmAll(true); setArmClear(false); return; }
    setArmAll(false);
    const before = ids;
    setIds([...ids, ...visibleNew.map((p) => p.id)]);
    onToast({ msg: `${visibleNew.length}건 담음`, undo: () => setIds(before) });
  };
  // 보이는(필터 · 검색 결과) 중 담긴 것 모두 빼기 — 필터가 「전체」면 전부 비우기 · 되돌리기 있음
  const visibleIn = rows.filter((p) => inSet.has(p.id));
  const clearVisible = () => {
    if (!armClear) { setArmClear(true); setArmAll(false); return; }
    setArmClear(false);
    const before = ids, off = new Set(visibleIn.map((p) => p.id));
    setIds(ids.filter((id) => !off.has(id)));
    onToast({ msg: `${off.size}건 뺌`, undo: () => setIds(before) });
  };
  const filterBySido = (s: string) => { setSido(s); setHideIn(true); setCat(''); setSt('all'); setReq(''); setQ(''); };

  return (
    <>
      {/* ① 필터 — 정부지원사업 탭 왼쪽 칸과 같은 모양(secTitle · filterItem · 지역 2열) */}
      <div style={{ width: 160, flexShrink: 0, borderRight: `1px solid ${C.border}`, overflowY: 'auto', padding: '14px 12px' }}>
        <div style={secTitle}>지역</div>
        <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {['', '전국', ...SIDO].map((r) => {
            const n = r ? sidoCount.get(r) || 0 : cands.length;
            if (r && !n) return null;
            return (
              <div key={r || 'all'} onClick={() => setSido(r)} title={r === '전국' ? '지역을 정하지 않은 공고' : r ? `${r} 공고` : '모든 공고'}
                style={{ ...filterItem(sido === r), ...(r ? { padding: '5px 6px', gap: 2 } : { gridColumn: '1 / -1', padding: '5px 8px' }) }}>
                <span>{r || '전체'}</span>
                <span style={{ fontSize: 11, color: sido === r ? C.accent : C.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
              </div>
            );
          })}
        </div>
        {sido && sido !== '전국' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted, cursor: 'pointer', margin: '-8px 0 16px' }}>
            <input type="checkbox" checked={withNation} onChange={(e) => setWithNation(e.target.checked)} style={{ accentColor: C.accent }} /> 전국 사업도 보기
          </label>
        )}
        <div style={secTitle}>분류</div>
        <div style={{ marginBottom: 16 }}>
          {[['', cands.length] as [string, number], ...cats].map(([c, n]) => (
            <div key={c || 'all'} onClick={() => setCat(c)} style={filterItem(cat === c)} title={c || '모든 분류'}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c ? c.replace('정책자금 · ', '자금 · ') : '전체'}</span>
              <span style={{ fontSize: 11, color: cat === c ? C.accent : C.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
            </div>
          ))}
        </div>
        <div style={secTitle} title="업종 · 지역 · 규모 말고 따로 갖춰야 하는 조건">자격 요건</div>
        <div style={{ marginBottom: 16 }}>
          {([['', '전체', cands.length], ['none', '요건 없음', reqCount.get('none') || 0], ['any', '요건 있음', reqCount.get('any') || 0]] as [ReqFilter, string, number][]).map(([k, l, n]) => (
            <div key={k || 'all'} onClick={() => setReq(k)} style={filterItem(req === k)}
              title={k === 'none' ? '업종 · 지역 · 규모만 맞으면 누구나 낼 수 있는 공고' : k === 'any' ? '업력 · 매출 · 대표 나이 · 창업 여부 · 전용 자격 · 확인 항목 중 하나라도 있는 공고' : '모든 공고'}>
              <span>{l}</span>
              <span style={{ fontSize: 11, color: req === k ? C.accent : C.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
            </div>
          ))}
          {REQ_KINDS.filter((k) => reqCount.get(k)).map((k) => (
            <div key={k} onClick={() => setReq(k)} style={{ ...filterItem(req === k), paddingLeft: 18 }} title={REQ_TIP[k]}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>└ {k}</span>
              <span style={{ fontSize: 11, color: req === k ? C.accent : C.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{reqCount.get(k)}</span>
            </div>
          ))}
        </div>
        <div style={secTitle}>모집 상태</div>
        <div style={{ marginBottom: 16 }}>
          {([['all', '전체'], ['urgent', '마감 임박'], ['later', `${Number(addYm(ym, 1).slice(5))}월 이후 접수`], ['always', '상시 · 예산 소진']] as [StFilter, string][]).map(([k, l]) => (
            <div key={k} onClick={() => setSt(k)} style={filterItem(st === k)}><span>{l}</span></div>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted, cursor: 'pointer' }}>
          <input type="checkbox" checked={hideIn} onChange={(e) => setHideIn(e.target.checked)} style={{ accentColor: C.accent }} /> 담은 것 숨기기
        </label>
      </div>

      {/* ② 후보 — 정부지원사업 탭 공고 카드와 같은 모양 · 왼쪽 네모 = 담김 */}
      <div style={{ flex: 1, minWidth: 380, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${C.border}` }}>
        <div style={{ padding: '12px 12px 0', flexShrink: 0 }}>
          <div role="tablist" aria-label="보기" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: R.md, background: '#E9EBEF', marginBottom: 8 }}>
            {([[false, '후보 전체', cands.length], [true, '담은 것', ids.length]] as [boolean, string, number][]).map(([v, l, n]) => (
              <button key={l} role="tab" aria-selected={onlyIn === v} onClick={() => { setOnlyIn(v); setCursor(0); setKept(v ? new Set(ids) : new Set()); if (v) setHideIn(false); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: onlyIn === v ? C.white : 'transparent', boxShadow: onlyIn === v ? '0 1px 2px rgba(17,24,39,0.10)' : 'none',
                  color: onlyIn === v ? C.text : C.sub, fontSize: T.sm, fontWeight: onlyIn === v ? 700 : 500 }}>
                {l}<span style={{ fontSize: T.xs, fontWeight: 700, color: onlyIn === v ? C.accent : C.muted, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
              </button>
            ))}
          </div>
          {onlyIn && ids.length > rowsIn && !q && !sido && !cat && st === 'all' && !req && (
            <span style={{ fontSize: T.xs, color: C.red, marginLeft: 10 }}>{ids.length - rowsIn}건은 마감 · 지난해 공고라 여기 안 보입니다 — 오른쪽 담은 목록에서 빼 주세요</span>
          )}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <div style={{ flex: 1 }} ref={searchWrap}><SearchInput value={q} onChange={setQ} placeholder="사업명·기관명  ( / )" /></div>
            <Btn variant={editing ? 'primary' : 'secondary'} onClick={() => setEditing(editing ? null : blankCustom())} title="이 소식지에만 쓰는 지원사업 — 정부지원사업 · 리포트에는 안 들어갑니다"
              style={{ fontSize: T.xs, padding: '6px 10px', whiteSpace: 'nowrap' }}>{editing ? '등록 닫기' : '+ 직접 등록'}</Btn>
            <Btn variant={armAll ? 'primary' : 'secondary'} onClick={addAllVisible} disabled={!visibleNew.length} style={{ fontSize: T.xs, padding: '6px 10px', whiteSpace: 'nowrap' }}>
              {armAll ? `${visibleNew.length}건 담을까요? 한 번 더` : `보이는 ${visibleNew.length}건 모두 담기`}
            </Btn>
            <Btn variant={armClear ? 'danger' : 'secondary'} onClick={clearVisible} disabled={!visibleIn.length} style={{ fontSize: T.xs, padding: '6px 10px', whiteSpace: 'nowrap' }}>
              {armClear ? `${visibleIn.length}건 뺄까요? 한 번 더` : `보이는 ${visibleIn.length}건 모두 빼기`}
            </Btn>
          </div>
          {editing && <CustomForm key={editing.id} init={editing} isNew={!customs.some((x) => x.id === editing.id)} cats={cats.map(([c]) => c.replace('정책자금 · ', ''))}
            onSave={saveCustom} onCancel={() => setEditing(null)} others={customs} onPick={(c) => setEditing(c)} />}
          <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 8 }}>
            <b style={{ color: C.sub }}>{onlyIn ? `담은 것 ${rowsIn}건` : `후보 ${rows.length}건`}</b>{onlyIn && rows.length > rowsIn ? <span style={{ color: C.orange }}> · 뺀 것 {rows.length - rowsIn}건은 다른 보기로 갈 때까지 남아 있어요(다시 누르면 담김)</span> : null} · 카드 클릭 = 담기/빼기 · Shift+클릭 = 범위 담기 · ↑↓ Space
          </div>
        </div>
        <div ref={listRef} tabIndex={0} onKeyDown={onKey} role="listbox" aria-multiselectable="true" aria-label="후보 공고"
          style={{ flex: 1, overflowY: 'auto', outline: 'none', padding: '0 12px 12px' }}>
          {!rows.length && <Empty icon="🏛️" message={onlyIn ? '아직 담은 지원사업이 없습니다 — 「후보 전체」에서 카드를 눌러 담으세요' : '조건에 맞는 공고가 없습니다'} />}
          {rows.map((p, i) => {
            const on = inSet.has(p.id), cur = i === cursor;
            return (
              <React.Fragment key={p.id}>
                {i === firstAlways && <div style={{ ...secTitle, margin: '10px 0 8px' }}>상시 접수 · 예산 소진 때까지</div>}
                <div data-i={i} role="option" aria-selected={on} onClick={(e) => onRowClick(i, e)}
                  style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: R.md, marginBottom: 6, cursor: 'pointer', userSelect: 'none',
                    background: on ? C.accentLt : C.white, border: `1px solid ${on ? C.accent : C.border}`,
                    boxShadow: cur ? `0 0 0 2px ${C.accent}33` : 'none',
                  }}>
                  <span aria-hidden style={{ flexShrink: 0, marginTop: 1, width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${on ? C.accent : C.borderDk}`, background: on ? C.accent : C.white, color: C.white, fontSize: 11, lineHeight: '14px', textAlign: 'center', fontWeight: 800 }}>{on ? '✓' : ''}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>
                      {isCustom(p.id) && <Badge bg={C.purpleLt} color={C.purple} style={{ marginRight: 6 }}>직접 등록</Badge>}
                      {isCustom(p.id)
                        ? <span role="button" tabIndex={0} title="눌러서 고치기" onClick={(e) => { e.stopPropagation(); const c = customs.find((x) => x.id === p.id); if (c) setEditing(c); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); const c = customs.find((x) => x.id === p.id); if (c) setEditing(c); } }}
                          style={{ textDecoration: 'underline', textDecorationColor: `${C.purple}66`, textUnderlineOffset: 3, cursor: 'text' }}>{shortName(p)} ✎</span>
                        : shortName(p)}
                    </div>
                    <div style={{ fontSize: 11, color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sidosOf(p).join('·')} · {p.agency || '-'} · {p.amount || '-'}</div>
                    <div style={{ fontSize: 11, color: C.sub, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span>{catOf(p).replace('정책자금 · ', '정책자금 ')}</span>
                      {periodOf(p) && <span style={{ fontVariantNumeric: 'tabular-nums' }}>· 모집 {periodOf(p)}</span>}
                      <StatusTag p={p} ym={ym} />
                      <span title="세무사 · FP가 고른 고객 업종에 이 업종이 있을 때만 나옵니다(업종 무관 = 모든 고객)" style={{ color: bizTop(p).length ? C.green : C.muted, fontWeight: 600 }}>
                        {bizTop(p).length ? `업종 ${bizTop(p).join('·')}` : '업종 무관'}
                      </span>
                      {sggList(p).length > 0 && <span title="이 시·군·구 고객에게만(시·군·구를 안 고른 사용자에게는 그 시·도 전체에 보임)" style={{ color: C.blue, fontWeight: 600 }}>{sggList(p).slice(0, 3).join('·')}{sggList(p).length > 3 ? ` 외 ${sggList(p).length - 3}` : ''} 한정</span>}
                      {(reqMap.get(p.id) || []).length > 0 && <span title={(reqMap.get(p.id) || []).map((k) => REQ_TIP[k]).join('\n')} style={{ color: C.purple, fontWeight: 600 }}>요건 {(reqMap.get(p.id) || []).join(' · ')}</span>}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <DDayBadge deadline={p.deadline} />
                    {isCustom(p.id) && (() => { const c = customs.find((x) => x.id === p.id); return c ? (
                      <span style={{ display: 'flex', gap: 4 }}>
                        <button onClick={(e) => { e.stopPropagation(); setEditing(c); }} style={{ fontSize: T.xs, color: C.purple, border: `1px solid ${C.purple}55`, background: C.white, borderRadius: R.sm, padding: '1px 6px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>고치기</button>
                        <button onClick={(e) => { e.stopPropagation(); setEditing({ ...c, id: newCustomId(), created_at: undefined }); }} title="같은 내용으로 새로 등록(지역만 바꿔 또 등록할 때)"
                          style={{ fontSize: T.xs, color: C.sub, border: `1px solid ${C.border}`, background: C.white, borderRadius: R.sm, padding: '1px 6px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>복사</button>
                        <button onClick={(e) => { e.stopPropagation(); removeCustom(c); }} style={{ ...linkBtn, fontSize: T.xs, color: C.red }}>지우기</button>
                      </span>
                    ) : null; })()}
                    {p.link && <a href={p.short_url || p.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="공고 원문 열기"
                      style={{ fontSize: T.xs, color: C.sub, textDecoration: 'none', border: `1px solid ${C.border}`, borderRadius: R.sm, padding: '1px 6px', background: C.white }}>원문 ↗</a>}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ③ 범위 + 충족도 360 */}
      <div style={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {importOpen
          ? <ImportPanel ym={ym} byId={byId} ids={ids} setIds={setIds} issues={issues} setIssues={setIssues} onClose={() => setImportOpen(false)} onToast={onToast} customs={customs} setCustoms={setCustoms} />
          : <PoolPanel ym={ym} pool={pool} cands={cands} ids={ids} setIds={setIds} onSido={filterBySido} onImport={() => setImportOpen(true)} missing={ids.length - pool.length} />}
      </div>
    </>
  );
}


// ═══ 범위 판(충족도 + 담은 목록) ═══
interface PoolPanelProps { ym: string; pool: GovProgram[]; cands: GovProgram[]; ids: string[]; setIds: (ids: string[]) => void; onSido: (s: string) => void; onImport: () => void; missing: number }
function PoolPanel({ ym, pool, cands, ids, setIds, onSido, onImport, missing }: PoolPanelProps) {
  const [openCov, setOpenCov] = React.useState(true);
  const nation = pool.filter((p) => sidosOf(p).includes('전국')).length;
  const perSido = SIDO.map((s) => ({ s, n: nation + pool.filter((p) => { const S = sidosOf(p); return S.includes(s) && !S.includes('전국'); }).length }));
  const short = perSido.filter((x) => x.n < 7);
  const catCount = new Map<string, number>(); pool.forEach((p) => catCount.set(catOf(p), (catCount.get(catOf(p)) || 0) + 1));
  const catAll = Array.from(new Set(cands.map(catOf)));
  const maxCat = Math.max(1, ...Array.from(catCount.values()));
  const expired = pool.filter((p) => !usable(p, ym));
  const urgent = pool.filter((p) => statusOf(p, ym) === 'urgent').length;
  const always = pool.filter((p) => !d10(p.deadline)).length;
  const sorted = pool.slice().sort(byDeadline);
  const tone = (n: number) => (n < 7 ? { bg: C.redLt, fg: C.red } : n < 10 ? { bg: C.orangeLt, fg: C.orange } : { bg: C.greenLt, fg: C.green });

  if (!ids.length) {
    return (
      <div style={{ margin: 'auto' }}>
        <Empty icon="📰" message={`${ymLabel(ym)}에 담은 사업이 없습니다 — 가운데 카드를 눌러 담거나 지난달 범위를 가져오세요`}
          action={<Btn onClick={onImport}>지난달에서 가져오기</Btn>} />
      </div>
    );
  }
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}>
      <div style={{ borderRadius: R.lg, background: C.white, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ padding: '10px 14px', borderBottom: openCov ? `1px solid ${C.border}` : 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>📍</span>
          <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text, flex: 1 }}>지역 충족도</span>
          {short.length ? <Badge bg={C.redLt} color={C.red}>부족 {short.length}곳</Badge> : <Badge bg={C.greenLt} color={C.green}>모두 7건 이상</Badge>}
          <button onClick={() => setOpenCov((v) => !v)} style={{ ...linkBtn, color: C.muted, fontSize: T.xs }}>{openCov ? '접기' : '펼치기'}</button>
        </div>
        {openCov && (
          <div style={{ padding: '10px 14px 12px' }}>
            <div style={{ fontSize: T.xs, color: C.sub }}>총 {pool.length}건 · 전국 {nation} · 마감 임박 {urgent} · 상시 {always}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 4, marginTop: 8 }}>
              {perSido.map(({ s: sd, n }) => {
                const t = tone(n);
                return <button key={sd} onClick={() => onSido(sd)} title={`${sd} 공고 중 아직 안 담은 것 보기`}
                  style={{ border: `1px solid ${t.fg}22`, borderRadius: R.sm, background: t.bg, color: t.fg, fontSize: T.xs, fontWeight: 600, padding: '5px 0', cursor: 'pointer', fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit' }}>{sd} {n}</button>;
              })}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>칸 숫자 = 그 지역 사업 + 전국 사업 · 7건 미만 빨강 · 누르면 그 지역 후보</div>
            <div style={{ ...secTitle, margin: '12px 0 6px' }}>분류</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {catAll.map((c) => {
                const n = catCount.get(c) || 0;
                return (
                  <div key={c} style={{ display: 'grid', gridTemplateColumns: '104px 1fr 22px', alignItems: 'center', gap: 6, fontSize: 11 }}>
                    <span style={{ color: n ? C.sub : C.red, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.replace('정책자금 · ', '자금 · ')}</span>
                    <span style={{ height: 5, background: C.bg, borderRadius: 3 }}><span style={{ display: 'block', height: 5, width: `${(n / maxCat) * 100}%`, background: C.accent, borderRadius: 3 }} /></span>
                    <span style={{ textAlign: 'right', color: n ? C.text : C.red, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {(expired.length > 0 || missing > 0) && (
        <div style={{ background: C.redLt, color: C.red, border: `1px solid ${C.red}33`, borderRadius: R.md, fontSize: T.xs, fontWeight: 600, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ flex: 1 }}>⚠ {expired.length > 0 && `기간 지남 ${expired.length}건`}{expired.length > 0 && missing > 0 && ' · '}{missing > 0 && `내려간 공고 ${missing}건`}</span>
          <Btn variant="danger" onClick={() => { const bad = new Set(expired.map((p) => p.id)); setIds(ids.filter((id) => !bad.has(id) && pool.some((p) => p.id === id))); }} style={{ fontSize: T.xs, padding: '4px 10px' }}>빼기</Btn>
        </div>
      )}
      <div style={secTitle}>담은 사업 {pool.length}건 <span style={{ color: C.muted, fontWeight: 400 }}>· 마감 빠른 순</span></div>
      {sorted.map((p) => (
        <div key={p.id} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', background: C.white, border: `1px solid ${usable(p, ym) ? C.border : C.red}`, borderRadius: R.md, padding: '8px 10px', marginBottom: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: T.sm, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.name}>{shortName(p)}</div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 2, display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{sidosOf(p).join('·')} · {periodOf(p) || '상시'}</span><StatusTag p={p} ym={ym} />
            </div>
          </div>
          <button aria-label={`${shortName(p)} 빼기`} title="빼기" onClick={() => setIds(ids.filter((x) => x !== p.id))}
            style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: '2px 6px', cursor: 'pointer', fontSize: T.xs, color: C.sub, flexShrink: 0 }}>빼기</button>
        </div>
      ))}
    </div>
  );
}

// ═══ 지난달에서 가져오기 ═══
interface ImportPanelProps { ym: string; byId: Map<string, GovProgram>; ids: string[]; setIds: (ids: string[]) => void; issues: NewsletterIssue[]; setIssues: (L: NewsletterIssue[]) => void; onClose: () => void; onToast: (t: { msg: string; undo?: () => void }) => void; customs: NewsletterCustom[]; setCustoms: (L: NewsletterCustom[]) => void }
function ImportPanel({ ym, byId: byIdHere, ids, setIds, issues, setIssues, onClose, onToast, customs, setCustoms }: ImportPanelProps) {
  const prev = addYm(ym, -1);
  const [src, setSrc] = React.useState<NewsletterMonth | null>(null);
  const [err, setErr] = React.useState('');
  const [offP, setOffP] = React.useState<Set<string>>(new Set());
  const [offI, setOffI] = React.useState<Set<string>>(new Set());
  const [showGone, setShowGone] = React.useState(false);
  React.useEffect(() => { getNewsletter(prev).then((r) => setSrc(r.data)).catch((e: unknown) => setErr(e instanceof Error ? e.message : '불러오지 못했습니다')); }, [prev]);
  if (err) return <div style={{ padding: 16, color: C.red, fontSize: T.sm }}>{err}</div>;
  if (!src) return <div style={{ padding: 16, color: C.muted, fontSize: T.sm }}>{ymLabel(prev)} 불러오는 중…</div>;
  const srcCustoms = src.customs || [];
  const byId = new Map(byIdHere); srcCustoms.forEach((c) => { if (!byId.has(c.id)) byId.set(c.id, customProg(c)); });   // 지난달 직접 등록도 함께
  const have = new Set(ids), haveI = new Set(issues.map((i) => i.id));
  const okP = src.program_ids.map((id) => byId.get(id)).filter((p): p is GovProgram => !!p && usable(p, ym) && !have.has(p.id)).sort(byDeadline);
  const gone = src.program_ids.filter((id) => { const p = byId.get(id); return !p || !usable(p, ym); });
  const okI = src.issues.filter((i) => !haveI.has(i.id));
  const tog = (s: Set<string>, id: string, set: (x: Set<string>) => void) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); set(n); };
  const run = () => {
    const addP = okP.filter((p) => !offP.has(p.id)).map((p) => p.id);
    const addI = okI.filter((i) => !offI.has(i.id)).map((i) => ({ ...i, star: false }));
    const beforeP = ids, beforeI = issues, beforeC = customs;
    const haveC = new Set(customs.map((c) => c.id));
    const addC = srcCustoms.filter((c) => addP.includes(c.id) && !haveC.has(c.id));
    if (addC.length) setCustoms([...addC, ...customs]);
    if (addP.length) setIds([...ids, ...addP]);
    if (addI.length) setIssues([...issues, ...addI]);
    onClose();
    onToast({ msg: `지원사업 ${addP.length}건 · 이슈 ${addI.length}건 가져옴`, undo: () => { setIds(beforeP); setIssues(beforeI); setCustoms(beforeC); } });
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: 'calc(100% - 24px)', margin: 12, background: C.white, border: `1px solid ${C.border}`, borderRadius: R.lg, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>📥 {ymLabel(prev)}에서 가져오기</div>
        <div style={{ fontSize: T.xs, color: C.sub, marginTop: 3 }}>이번 달에도 유효한 지원사업 {okP.length}건 · 이슈 {okI.length}건</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px' }}>
        {!src.exists && <div style={{ color: C.muted, fontSize: T.sm, padding: '12px 0' }}>{ymLabel(prev)}에는 저장된 범위가 없습니다</div>}
        {okP.map((p) => (
          <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', fontSize: T.sm, color: C.text, borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}>
            <input type="checkbox" checked={!offP.has(p.id)} onChange={() => tog(offP, p.id, setOffP)} style={{ marginTop: 2, accentColor: C.accent }} />
            <span style={{ minWidth: 0 }}><b>{shortName(p)}</b><br /><span style={{ color: C.muted, fontSize: T.xs }}>{sidosOf(p).join('·')} · {periodOf(p) || '상시'}</span></span>
          </label>
        ))}
        {okI.length > 0 && <div style={{ ...secTitle, margin: '14px 0 4px' }}>이달의 이슈</div>}
        {okI.map((i) => (
          <label key={i.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 0', fontSize: T.sm, borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}>
            <input type="checkbox" checked={!offI.has(i.id)} onChange={() => tog(offI, i.id, setOffI)} style={{ marginTop: 2, accentColor: C.accent }} />
            <span>[{i.kind}] {i.title.replace(/\n/g, ' ')}</span>
          </label>
        ))}
        {gone.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button onClick={() => setShowGone((v) => !v)} style={{ ...linkBtn, color: C.muted, fontSize: T.xs }}>제외 {gone.length}건(기간 지남 · 내려감) {showGone ? '접기' : '보기'}</button>
            {showGone && gone.map((id) => { const p = byId.get(id); return <div key={id} style={{ fontSize: T.xs, color: C.muted, padding: '2px 0' }}>{p ? `${shortName(p)} · 마감 ${md(d10(p.deadline))}` : '목록에서 내려간 공고'}</div>; })}
          </div>
        )}
      </div>
      <div style={{ padding: 12, borderTop: `1px solid ${C.border}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn variant="secondary" onClick={onClose} style={{ flex: 1 }}>닫기</Btn>
        <Btn onClick={run} disabled={!okP.length && !okI.length} style={{ flex: 2 }}>가져오기</Btn>
      </div>
    </div>
  );
}

// ═══ 직접 등록 폼 — 사업명만 있으면 됨(나머지는 소식지 한 줄에 보일 것) ═══
interface CustomFormProps { init: NewsletterCustom; isNew: boolean; cats: string[]; onSave: (c: NewsletterCustom) => void; onCancel: () => void; others: NewsletterCustom[]; onPick: (c: NewsletterCustom) => void }
function CustomForm({ init, isNew, cats, onSave, onCancel, others, onPick }: CustomFormProps) {
  const [f, setF] = React.useState<NewsletterCustom>({ ...init, areas: areasOf(init), sizes: init.sizes || [], biz: init.biz || [] });
  const A = f.areas || {};
  const [openSido, setOpenSido] = React.useState<string>(SIDO.find((x) => A[x]) || '');   // 시·군·구를 펼쳐 보는 시·도
  const flip = (L: string[] | undefined, v: string): string[] => ((L || []).includes(v) ? (L || []).filter((x) => x !== v) : [...(L || []), v]);
  const toggleSido = (sd: string) => {
    const n = { ...A };
    if (n[sd]) { delete n[sd]; if (openSido === sd) setOpenSido(SIDO.find((x) => n[x]) || ''); } else { n[sd] = []; setOpenSido(sd); }
    setF((x) => ({ ...x, areas: n }));
  };
  const toggleSgg = (sd: string, g: string) => setF((x) => ({ ...x, areas: { ...(x.areas || {}), [sd]: flip((x.areas || {})[sd], g) } }));
  const chip = (on: boolean): React.CSSProperties => ({ border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accent : C.white, color: on ? C.white : C.text, borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.5 });
  const rowLab: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: C.sub, width: 64, flexShrink: 0, paddingTop: 4 };
  const set = (p: Partial<NewsletterCustom>) => setF((x) => ({ ...x, ...p }));
  const [err, setErr] = React.useState('');
  const submit = () => {
    const name = f.name.trim();
    if (!name) { setErr('사업명을 적어 주세요'); return; }
    let st = f.apply_start || null, dl = f.deadline || null;
    if (st && dl && st > dl) [st, dl] = [dl, st];
    const link = f.link.trim();
    const ar = f.areas || {};
    onSave({ ...f, name, agency: f.agency.trim(), amount: f.amount.trim(), category: f.category.trim(), apply_start: st, deadline: dl, first_come: dl ? false : f.first_come, link: /^https?:\/\//i.test(link) ? link : '',
      areas: ar, region: SIDO.filter((x) => ar[x]).join(',') || '전국', region_detail: SIDO.flatMap((x) => ar[x] || []).join(',') });
  };
  const lab: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: C.sub, marginBottom: 3, display: 'block' };
  const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', border: `1px solid ${C.border}`, borderRadius: R.sm, padding: '6px 8px', fontSize: T.sm, fontFamily: 'inherit', background: C.white, color: C.text };
  return (
    <div onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); submit(); } if (e.key === 'Escape') onCancel(); }}
      style={{ border: `1px solid ${C.purple}55`, background: C.purpleLt, borderRadius: R.md, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <b style={{ fontSize: T.sm, color: C.text }}>{isNew ? (init.name ? '복사해서 새로 등록' : '지원사업 직접 등록') : '직접 등록 고치기'}</b>
        <span style={{ fontSize: 11, color: C.sub }}>이 소식지에만 쓰입니다 — 정부지원사업 탭 · 리포트 · 매칭에는 들어가지 않아요</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
        <label><span style={lab}>사업명 *</span><IMEText autoFocus value={f.name} onChange={(v) => { set({ name: v }); setErr(''); }} maxLength={40} placeholder="예: 청년 디지털 일자리" style={inp} /></label>
        <label><span style={lab}>지원 한도</span><IMEText value={f.amount} onChange={(v) => set({ amount: v })} maxLength={40} placeholder="예: 최대 5천만원" style={inp} /></label>
        <label><span style={lab}>기관</span><IMEText value={f.agency} onChange={(v) => set({ agency: v })} maxLength={40} placeholder="예: 중소벤처기업부" style={inp} /></label>
        <label><span style={lab}>분류</span>
          <IMEText value={f.category} onChange={(v) => set({ category: v })} maxLength={20} list="nl-custom-cats" placeholder="예: 정책자금 · 수출" style={inp} />
          <datalist id="nl-custom-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist>
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          <label style={{ flex: 1 }}><span style={lab}>접수 시작</span><input type="date" value={f.apply_start || ''} onChange={(e) => set({ apply_start: e.target.value || null })} style={inp} /></label>
          <label style={{ flex: 1 }}><span style={lab}>마감</span><input type="date" value={f.deadline || ''} onChange={(e) => set({ deadline: e.target.value || null })} style={inp} /></label>
        </div>
        <label style={{ gridColumn: '1 / 2' }}><span style={lab}>원문 링크</span><IMEText value={f.link} onChange={(v) => set({ link: v })} placeholder="https://" style={inp} /></label>
        <label style={{ display: 'flex', alignItems: 'flex-end', gap: 5, fontSize: T.xs, color: f.deadline ? C.muted : C.sub, paddingBottom: 7 }} title="마감일이 없을 때 — 소식지에 「예산 소진 때까지」 · 없으면 「상시」">
          <input type="checkbox" disabled={!!f.deadline} checked={!f.deadline && f.first_come} onChange={(e) => set({ first_come: e.target.checked })} style={{ accentColor: C.accent }} /> 마감 없음 · 예산 소진 때까지
        </label>
      </div>
      {/* 추천 조건 — 세무사 · FP가 고른 고객 조건(업종 · 규모 · 지역)과 맞을 때만 그 소식지에 나온다 (2026-10-10 지도사 「소상공인 · 소기업 · 중기업 구분과 업종 구분 · 지역도 시군구까지 · 복수 선택」) */}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.purple}44`, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={rowLab}>업종</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            <button type="button" onClick={() => set({ biz: [] })} style={chip(!(f.biz || []).length)}>업종 무관</button>
            {Object.keys(NL_BIZ).map((g) => <button type="button" key={g} onClick={() => set({ biz: flip(f.biz, g) })} style={chip((f.biz || []).includes(g))}>{g}</button>)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={rowLab}>기업 규모</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            <button type="button" onClick={() => set({ sizes: [] })} style={chip(!(f.sizes || []).length)}>규모 무관</button>
            {NL_SIZES.map((z) => <button type="button" key={z} onClick={() => set({ sizes: flip(f.sizes, z) })} style={chip((f.sizes || []).includes(z))}>{z}</button>)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={rowLab}>지역</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              <button type="button" onClick={() => { set({ areas: {} }); setOpenSido(''); }} style={chip(!Object.keys(A).length)}>전국</button>
              {SIDO.map((sd) => (
                <button type="button" key={sd} onClick={() => toggleSido(sd)} style={{ ...chip(!!A[sd]), ...(A[sd] && openSido === sd ? { boxShadow: `0 0 0 2px ${C.accent}44` } : {}) }}>
                  {sd}{A[sd] && A[sd].length ? ` ${A[sd].length}` : ''}
                </button>
              ))}
            </div>
            {Object.keys(A).length > 0 && (
              <div style={{ marginTop: 6, background: C.white, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: 8 }}>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                  {SIDO.filter((sd) => A[sd]).map((sd) => (
                    <button type="button" key={sd} onClick={() => setOpenSido(sd)} style={{ ...linkBtn, fontSize: 12, fontWeight: openSido === sd ? 800 : 500, color: openSido === sd ? C.accent : C.sub, borderBottom: openSido === sd ? `2px solid ${C.accent}` : '2px solid transparent', padding: '0 4px 2px' }}>
                      {sd} · {A[sd].length ? A[sd].join('·') : '전체'}
                    </button>
                  ))}
                </div>
                {openSido && A[openSido] && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <button type="button" onClick={() => setF((x) => ({ ...x, areas: { ...(x.areas || {}), [openSido]: [] } }))} style={chip(!A[openSido].length)}>{openSido} 전체</button>
                    {(REGION_CITIES[openSido] || []).map((g) => <button type="button" key={g} onClick={() => toggleSgg(openSido, g)} style={chip(A[openSido].includes(g))}>{g}</button>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ fontSize: 11, color: C.sub }}>세무사 · FP가 고른 고객의 업종 · 규모 · 지역과 맞을 때만 그 소식지 추천에 나옵니다 · 지금: {(f.biz || []).length ? (f.biz || []).join('·') : '업종 무관'} · {(f.sizes || []).length ? (f.sizes || []).join('·') : '규모 무관'} · {areaText(A)}</div>
      </div>
      {others.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: C.sub, fontWeight: 600 }}>이미 등록한 것 {others.length} · 눌러서 고치기</span>
          {others.map((c) => (
            <button type="button" key={c.id} onClick={() => onPick(c)} title={`${c.name} 고치기`}
              style={{ ...chip(c.id === f.id), background: c.id === f.id ? C.purple : C.white, borderColor: c.id === f.id ? C.purple : C.border, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
        {err && <span role="alert" style={{ fontSize: T.xs, color: C.red, fontWeight: 600 }}>{err}</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {!isNew && <Btn variant="secondary" onClick={() => onPick({ ...f, id: newCustomId(), created_at: undefined })} title="지금 내용 그대로 새 등록으로(지역만 바꿔 또 등록할 때) — 원래 것은 그대로" style={{ fontSize: T.xs, padding: '6px 12px' }}>복사해서 새로 등록</Btn>}
          <Btn variant="secondary" onClick={onCancel} style={{ fontSize: T.xs, padding: '6px 12px' }}>취소</Btn>
          <Btn onClick={submit} style={{ fontSize: T.xs, padding: '6px 14px' }}>{isNew ? '등록하고 담기' : '고친 내용 저장'}</Btn>
        </span>
      </div>
    </div>
  );
}

// ═══ 이달의 이슈 탭 ═══
interface IssueTabProps {
  issues: NewsletterIssue[]; setIssues: (L: NewsletterIssue[]) => void; importOpen: boolean; setImportOpen: (v: boolean) => void;
  ym: string; byId: Map<string, GovProgram>; ids: string[]; setIds: (ids: string[]) => void; onToast: (t: { msg: string; undo?: () => void }) => void;
  customs: NewsletterCustom[]; setCustoms: (L: NewsletterCustom[]) => void;
}
const LIM = { label: 60, caption: 16, title: 60, body: 300, action: 80 };
function IssueTab({ issues, setIssues, importOpen, setImportOpen, ym, byId, ids, setIds, onToast, customs, setCustoms }: IssueTabProps) {
  const [sel, setSel] = React.useState<string>(issues[0] ? issues[0].id : '');
  const cur = issues.find((i) => i.id === sel) || null;
  const patch = (p: Partial<NewsletterIssue>) => { if (!cur) return; setIssues(issues.map((i) => (i.id === cur.id ? { ...i, ...p } : i))); };
  const add = () => { const n = blankIssue(); if (!issues.length) n.star = true; setIssues([...issues, n]); setSel(n.id); };
  const remove = (id: string) => {
    const before = issues, idx = issues.findIndex((i) => i.id === id);
    const next = issues.filter((i) => i.id !== id);
    if (before[idx] && before[idx].star && next[0]) next[0] = { ...next[0], star: true };
    setIssues(next); setSel(next[Math.max(0, idx - 1)] ? next[Math.max(0, idx - 1)].id : '');
    onToast({ msg: '이슈 1건 지움', undo: () => { setIssues(before); setSel(id); } });
  };
  const move = (id: string, k: number) => {
    const i = issues.findIndex((x) => x.id === id), j = i + k;
    if (i < 0 || j < 0 || j >= issues.length) return;
    const L = issues.slice(); [L[i], L[j]] = [L[j], L[i]]; setIssues(L);
  };
  const star = (id: string) => setIssues(issues.map((i) => (i.id === id ? { ...i, star: true, off: false } : { ...i, star: false })));   // ★ 는 체크된 것 중 하나 — 체크 안 된 것에 누르면 체크도
  // 체크 = 세무사 · fphub 에서 고를 수 있는 이슈(여러 개) · 체크 해제 = 보관만 (2026-10-10 지도사 「체크를 여러 개 하도록 하고 체크된 거는 유저가 고르는 거」)
  const toggleOn = (id: string) => {
    let L = issues.map((i) => (i.id === id ? { ...i, off: !i.off, star: i.off ? i.star : false } : i));
    if (!L.some((i) => i.star && !i.off)) { const f = L.find((i) => !i.off); if (f) L = L.map((i) => ({ ...i, star: i.id === f.id })); }
    setIssues(L);
  };
  const nOn = issues.filter((i) => !i.off).length;
  // 「📚 법령·판례·뉴스」에서 추천 받아 담기 (2026-10-09 지도사) — 초안을 채운 새 이슈 · 되돌리기 있음
  const [ideasOpen, setIdeasOpen] = React.useState(false);
  const addIdea = (d: NewsletterIdea['draft']): string => {
    const n: NewsletterIssue = { ...blankIssue(), ...d, kind: (KINDS as string[]).includes(d.kind) ? d.kind : '법령', star: !issues.length };
    const before = issues;
    setIssues([...issues, n]);
    onToast({ msg: '추천 이슈 1건 담음 · 행동 지침은 직접 적어 주세요', undo: () => setIssues(before) });
    return n.id;
  };

  return (
    <>
      <div style={{ width: 280, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '12px 12px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Btn onClick={() => { add(); setIdeasOpen(false); }} style={{ width: '100%', fontSize: T.xs, padding: '7px 10px' }}>+ 새 이슈</Btn>
          <Btn variant={ideasOpen ? 'primary' : 'secondary'} onClick={() => setIdeasOpen(!ideasOpen)} style={{ width: '100%', fontSize: T.xs, padding: '7px 10px' }}>📚 법령·판례·뉴스에서 추천</Btn>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {!issues.length && <Empty icon="📝" message="이달의 이슈 후보를 적어 두세요 · 체크한 이슈 중에서 소식지를 만드는 사람이 하나를 고릅니다(★ = 따로 안 고르면 쓰는 기본 이슈)" />}
          {issues.length > 0 && <div style={{ fontSize: 11, color: C.sub, marginBottom: 8, lineHeight: 1.5 }}>☑ 체크한 <b style={{ color: C.accent }}>{nOn}건</b>을 세무사 · FP가 골라 씁니다 · ★ = 안 고르면 기본</div>}
          {issues.map((i, k) => (
            <div key={i.id} onClick={() => { setSel(i.id); setIdeasOpen(false); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') { setSel(i.id); setIdeasOpen(false); } }}
              style={{ border: `1px solid ${i.id === sel ? C.accent : C.border}`, background: i.id === sel ? C.accentLt : C.white, borderRadius: R.md, padding: '10px 12px', marginBottom: 6, cursor: 'pointer', opacity: i.off ? 0.6 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={!i.off} onClick={(e) => e.stopPropagation()} onChange={() => toggleOn(i.id)} aria-label="세무사 · FP가 고를 수 있게"
                  title={i.off ? '체크하면 세무사 · FP가 고를 수 있습니다' : '체크 해제하면 보관만(고를 수 없음)'} style={{ accentColor: C.accent, width: 15, height: 15, margin: 0, cursor: 'pointer' }} />
                <Badge bg={C.accentLt} color={C.accent}>{i.kind}</Badge>
                <button aria-label="기본 이슈로" title="기본 이슈(따로 안 고르면 이 이슈)" onClick={(e) => { e.stopPropagation(); star(i.id); }} style={{ ...linkBtn, color: i.star ? C.orange : C.borderDk, fontSize: T.lg, lineHeight: 1 }}>★</button>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                  <button aria-label="위로" disabled={k === 0} onClick={(e) => { e.stopPropagation(); move(i.id, -1); }} style={{ ...linkBtn, color: k === 0 ? C.border : C.muted }}>▲</button>
                  <button aria-label="아래로" disabled={k === issues.length - 1} onClick={(e) => { e.stopPropagation(); move(i.id, 1); }} style={{ ...linkBtn, color: k === issues.length - 1 ? C.border : C.muted }}>▼</button>
                </span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: i.title ? C.text : C.muted }}>{i.title ? i.title.replace(/\n/g, ' ') : '(제목 없음)'}</div>
              {i.off && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>보관만 · 세무사 · FP 화면에 안 보임</div>}
            </div>
          ))}
        </div>
      </div>

      {ideasOpen
        ? <IdeasPanel ym={ym} issues={issues} onAdd={addIdea} onEdit={(id) => { setSel(id); setIdeasOpen(false); }} onClose={() => setIdeasOpen(false)} />
        : !cur
        ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: T.md }}>← 이슈를 고르거나 「+ 새 이슈」 · 「📚 추천」을 누르세요</div>
        : (
            <>
              <div style={{ flex: 1, minWidth: 360, overflowY: 'auto', padding: '16px 20px' }}>
               <div key={cur.id} style={{ borderRadius: R.lg, background: C.white, border: `1px solid ${C.border}`, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <FieldLabel>구분</FieldLabel>
                  <div role="radiogroup" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: R.md, background: '#E9EBEF' }}>
                    {KINDS.map((k) => { const on = cur.kind === k; return <button key={k} role="radio" aria-checked={on} onClick={() => patch({ kind: k })} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: T.sm, fontWeight: on ? 700 : 500, background: on ? C.white : 'transparent', boxShadow: on ? '0 1px 2px rgba(17,24,39,0.10)' : 'none', color: on ? C.text : C.sub }}>{k}</button>; })}
                  </div>
                </div>
                <TextRow label="출처 한 줄" hint="법령 · 2026 세법개정안 · 9월 국회 제출" value={cur.label} max={LIM.label} onChange={(v) => patch({ label: v })} />
                <TextRow label="머리 오른쪽 작은 글" hint="세법개정안" value={cur.caption} max={LIM.caption} onChange={(v) => patch({ caption: v })} />
                <TextRow label="제목 (Enter 로 줄바꿈 · 2줄까지)" hint={'증빙 없는 경조사비 한도\n20만 → 30만원으로'} value={cur.title} max={LIM.title} rows={2} onChange={(v) => patch({ title: v })} />
                <TextRow label="본문 (4줄까지)" hint="국회를 통과하면 2027년부터 적용됩니다." value={cur.body} max={LIM.body} rows={4} onChange={(v) => patch({ body: v })} />
                <TextRow label="행동 지침 한 줄" hint="→ 청첩장·부고 문자는 지출 증빙으로 보관" value={cur.action} max={LIM.action} onChange={(v) => patch({ action: v })} />
                <div>
                  <FieldLabel>원문 링크</FieldLabel>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <IMEText value={cur.link} onChange={(v) => patch({ link: v.trim() })} placeholder="https://" style={inputSt} />
                    {/^https?:\/\//.test(cur.link) && <a href={cur.link} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', borderRadius: R.md, fontSize: T.sm, fontWeight: 600, background: C.white, color: C.text, border: `1px solid ${C.border}`, textDecoration: 'none', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>열어보기 ↗</a>}
                  </div>
                  {cur.link && !/^https?:\/\//.test(cur.link) && <div style={{ fontSize: T.xs, color: C.red, marginTop: 4 }}>http:// 또는 https:// 로 시작해야 저장됩니다</div>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Btn variant="danger" onClick={() => remove(cur.id)} style={{ fontSize: T.xs, padding: '6px 10px' }}>이 이슈 지우기</Btn>
                </div>
               </div>
              </div>
              {!importOpen && (
                <div style={{ width: 380, flexShrink: 0, borderLeft: `1px solid ${C.border}`, overflowY: 'auto', padding: '16px 12px' }}>
                  <div style={secTitle}>안내장 미리보기 · 실제 크기(왼쪽 아래 칸)</div>
                  <IssuePreview it={cur} />
                </div>
              )}
            </>
          )}
      {importOpen && (
        <div style={{ width: 340, flexShrink: 0, borderLeft: `1px solid ${C.border}`, minHeight: 0 }}>
          <ImportPanel ym={ym} byId={byId} ids={ids} setIds={setIds} issues={issues} setIssues={setIssues} onClose={() => setImportOpen(false)} onToast={onToast} customs={customs} setCustoms={setCustoms} />
        </div>
      )}
    </>
  );
}
function FieldLabel({ children }: { children: React.ReactNode }) { return <label style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 3 }}>{children}</label>; }
function TextRow({ label, hint, value, max, rows, onChange }: { label: string; hint: string; value: string; max: number; rows?: number; onChange: (v: string) => void }) {
  const over = value.length > max;
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      {rows
        ? <IMEArea value={value} rows={rows} placeholder={hint} onChange={onChange} style={{ ...inputSt, height: 'auto', resize: 'vertical', lineHeight: 1.6, fontFamily: 'inherit' }} />
        : <IMEText value={value} placeholder={hint} onChange={onChange} style={inputSt} />}
      <div style={{ fontSize: T.xs, color: over ? C.red : C.muted, textAlign: 'right', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value.length} / {max}{over && ' · 넘친 글자는 저장할 때 잘립니다'}</div>
    </div>
  );
}

/** 안내장 왼쪽 아래 「이달의 이슈」 칸 — A4(794px) 안 칸 폭 325px 그대로 · 제목 2줄 · 본문 3줄 넘침 표시 (안내장 시안 brief.js 와 같은 글자 크기) */
function IssuePreview({ it }: { it: NewsletterIssue }) {
  const tRef = React.useRef<HTMLDivElement>(null), bRef = React.useRef<HTMLDivElement>(null);
  const [lines, setLines] = React.useState<{ t: number; b: number }>({ t: 0, b: 0 });
  React.useLayoutEffect(() => {
    const t = tRef.current, b = bRef.current;
    setLines({ t: t ? Math.round(t.scrollHeight / (18 * 1.45)) : 0, b: b ? Math.round(b.scrollHeight / (14 * 1.7)) : 0 });   // 안내장 판(2026-10-09 디자인 핸드오프) 크기와 같게
  }, [it.title, it.body]);
  const A = '#1d2a44';
  const warn = (s: string) => <div style={{ fontSize: 12, color: C.red, marginTop: 4, fontFamily: 'inherit' }}>{s}</div>;
  return (
    <div style={{ width: 321, background: '#fff', padding: '14px 16px 16px', borderRadius: R.lg, border: `1px solid ${C.border}`, fontFamily: "'Pretendard Variable','Pretendard',sans-serif", color: '#1C1B1A', letterSpacing: '-0.01em', wordBreak: 'keep-all', overflowWrap: 'anywhere', boxSizing: 'content-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingBottom: 9, borderBottom: `1.5px solid ${A}` }}>
        <span style={{ fontSize: 21, fontWeight: 700, color: A, letterSpacing: '-0.02em' }}>이달의 이슈</span>
        <span style={{ fontSize: 13, color: '#6E6A64' }}>{it.caption}</span>
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: A, marginTop: 16 }}>{it.label || <span style={{ color: '#bbb' }}>출처 한 줄</span>}</div>
        <div ref={tRef} style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.45, marginTop: 8, letterSpacing: '-0.02em', whiteSpace: 'pre-line', outline: lines.t > 2 ? `2px dashed ${C.red}` : 'none' }}>{it.title || <span style={{ color: '#bbb' }}>제목</span>}</div>
        {lines.t > 2 && warn(`제목이 ${lines.t}줄입니다 — 2줄 안으로 줄여 주세요`)}
        <div ref={bRef} style={{ fontSize: 14, lineHeight: 1.7, color: '#4A4743', marginTop: 10, outline: lines.b > 4 ? `2px dashed ${C.red}` : 'none' }}>{it.body || <span style={{ color: '#bbb' }}>본문</span>}</div>
        {lines.b > 4 && warn(`본문이 ${lines.b}줄입니다 — 4줄 안으로 줄여 주세요`)}
        <div style={{ fontSize: 14, fontWeight: 700, marginTop: 14 }}>{it.action ? `→ ${it.action.replace(/^\s*(→|->)\s*/, '')}` : <span style={{ color: '#bbb' }}>→ 행동 지침</span>}</div>
      </div>
    </div>
  );
}

// ═══ 작은 스타일 ═══
const linkBtn: React.CSSProperties = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', font: 'inherit' };
const monthBtn: React.CSSProperties = { padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.md, color: C.text };
const inputSt: React.CSSProperties = { width: '100%', height: 38, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' };
/** 정부지원사업 탭(GovPage) 왼쪽 칸과 같은 모양 */
const secTitle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${C.border}` };
const filterItem = (active: boolean): React.CSSProperties => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '6px 10px', borderRadius: R.sm, cursor: 'pointer', fontSize: 12,
  fontWeight: active ? 600 : 400, color: active ? C.accent : C.text,
  background: active ? C.accentLt : 'transparent', transition: 'all 0.12s',
});

/** 이달의 이슈 추천 — 「법령·판례·뉴스」가 모은 글 중 점수 높은 15건(★ · 그 달 시행 · 세금 · 판례/해석 · 최근) · AI 호출 없음 */
const TAB_LABEL: Record<string, string> = { law: '법령 개정', prec: '판례', expc: '해석', bill: '국회 법안', notice: '입법예고', cabinet: '국무회의', news: '뉴스' };
function IdeasPanel({ ym, issues, onAdd, onEdit, onClose }: { ym: string; issues: NewsletterIssue[]; onAdd: (d: NewsletterIdea['draft']) => string; onEdit: (id: string) => void; onClose: () => void }) {
  const [list, setList] = React.useState<NewsletterIdea[] | null>(null);
  const [error, setError] = React.useState('');
  const [added, setAdded] = React.useState<Record<string, string>>({});   // ref 키 → 담은 이슈 id
  React.useEffect(() => {
    let alive = true;
    setList(null); setError('');
    getNewsletterIdeas(ym).then((r) => { if (alive) setList(r.data || []); }).catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : '추천을 불러오지 못했습니다'); });
    return () => { alive = false; };
  }, [ym]);
  const keyOf = (x: NewsletterIdea) => `${x.ref.kind}:${x.ref.id}`;
  const full = issues.length >= 20;
  return (
    <div style={{ flex: 1, minWidth: 420, overflowY: 'auto', padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: T.lg, fontWeight: 800, color: C.text }}>📚 이달의 이슈 추천</span>
        <span style={{ fontSize: T.xs, color: C.muted }}>{list ? `${list.length}건` : ''}</span>
        <button onClick={onClose} style={{ ...linkBtn, marginLeft: 'auto', fontSize: T.sm, color: C.muted }}>닫기 ✕</button>
      </div>
      <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 12, lineHeight: 1.6 }}>
        「법령·판례·뉴스」가 모은 글에서 고릅니다 · 그곳에서 ★ 표시한 글 · {Number(ym.slice(5))}월에 시행되는 법령 · 세금 관련 · 최근 글이 먼저 · 담으면 출처 · 제목 · 본문이 채워지고 행동 지침만 적으면 됩니다
      </div>
      {error && <div style={{ color: C.red, fontSize: T.sm }}>{error}</div>}
      {!list && !error && <div style={{ color: C.muted, fontSize: T.sm, padding: '20px 0' }}>불러오는 중…</div>}
      {list && !list.length && <Empty icon="📚" message="추천할 글이 없습니다 · 「법령·판례·뉴스」에서 쓸 만한 글에 ★ 를 눌러 두면 여기 먼저 나옵니다" />}
      {full && <div style={{ fontSize: T.xs, color: C.orange, marginBottom: 8 }}>이슈는 20건까지 담을 수 있습니다</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(list || []).map((x) => {
          const k = keyOf(x), done = added[k];
          return (
            <div key={k} style={{ border: `1px solid ${done ? C.green : C.border}`, background: C.white, borderRadius: R.md, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Badge bg={C.accentLt} color={C.accent}>{x.draft.kind}</Badge>
                <span style={{ fontSize: 11, color: C.sub, fontWeight: 600 }}>{TAB_LABEL[x.ref.tab] || x.ref.tab}</span>
                {x.why.filter((w) => w !== '판례' && w !== '해석').map((w) => <span key={w} style={{ fontSize: 11, padding: '1px 6px', borderRadius: 6, background: w.startsWith('★') ? '#FFF7ED' : '#F3F4F6', color: w.startsWith('★') ? C.orange : C.sub }}>{w}</span>)}
                <span style={{ marginLeft: 'auto', fontSize: 11, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{x.date}</span>
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{x.draft.label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 2, lineHeight: 1.4 }}>{x.draft.title}</div>
              {x.draft.body && <div style={{ fontSize: 12, color: C.sub, marginTop: 4, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{x.draft.body}</div>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                {x.draft.link && <a href={x.draft.link} target="_blank" rel="noreferrer" style={{ fontSize: T.xs, color: C.sub, textDecoration: 'none' }}>원문 ↗</a>}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  {done
                    ? <Btn variant="secondary" onClick={() => onEdit(done)} style={{ fontSize: T.xs, padding: '5px 10px' }}>담음 ✓ · 고치러 가기</Btn>
                    : <Btn onClick={() => { const id = onAdd(x.draft); setAdded((a) => ({ ...a, [k]: id })); }} disabled={full} style={{ fontSize: T.xs, padding: '5px 10px' }}>이슈로 담기</Btn>}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
